import { useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { TextInput } from './Field';

/**
 * Password field with a reveal toggle. The button is a real button with an
 * aria-label, so it is reachable by keyboard and announced by screen readers.
 */
export function PasswordInput({
  error, className, ...rest
}: InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <span className="relative block">
      <TextInput
        {...rest}
        type={visible ? 'text' : 'password'}
        error={error}
        className={cn('pe-10', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={t(visible ? 'auth.hidePassword' : 'auth.showPassword')}
        aria-pressed={visible}
        className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-ink-subtle transition-colors hover:bg-canvas-soft hover:text-ink"
      >
        {visible ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
      </button>
    </span>
  );
}
